/* eslint-disable max-lines -- legacy size; refactor planned in plans/cached-singing-fiddle.md */
import { createElement, type ReactNode, useMemo, memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { openExternalUrl } from "@/lib/open-url";
import { CodeBlock } from "@/components/messages/CodeBlock";

interface UserInfo {
  id: string;
  display_name: string;
}

interface ChannelInfo {
  id: string;
  name: string;
}

/**
 * Parses `@mentions` and `#channels` in a text string,
 * returning an array of string and ReactElement segments.
 */
function parseMentionsAndChannels(
  text: string,
  users: UserInfo[],
  onMentionClick?: (userId: string) => void,
  channels?: ChannelInfo[],
  onChannelClick?: (channelId: string) => void,
  keyPrefix = "md",
): (string | React.ReactElement)[] {
  const hasUsers = users.length > 0;
  const hasChannels = (channels?.length ?? 0) > 0;
  if (!hasUsers && !hasChannels) return parseHighlightSyntax(text, keyPrefix);

  const sortedUsers = [...users].sort(
    (a, b) => b.display_name.length - a.display_name.length,
  );
  const sortedChannels = channels
    ? [...channels].sort((a, b) => b.name.length - a.name.length)
    : [];

  const patterns: string[] = [];
  if (hasUsers) {
    patterns.push(...sortedUsers.map((u) => `@${escapeRegex(u.display_name)}`));
  }
  if (hasChannels) {
    patterns.push(
      ...sortedChannels.map((ch) => `(?<=^|\\s)#${escapeRegex(ch.name)}`),
    );
  }
  const regex = new RegExp(`(${patterns.join("|")})`, "g");
  const parts = text.split(regex);

  const result: (string | React.ReactElement)[] = [];
  parts.forEach((part, i) => {
    if (part.startsWith("@")) {
      const name = part.slice(1);
      const matchedUser = users.find((u) => u.display_name === name);
      if (matchedUser) {
        result.push(
          createElement(
            "span",
            {
              key: `${keyPrefix}-m${i}`,
              className: `text-warning rounded px-0.5 font-medium${onMentionClick ? " cursor-pointer hover:underline" : ""}`,
              ...(onMentionClick && {
                role: "button",
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onMentionClick(matchedUser.id);
                },
              }),
            },
            part,
          ),
        );
        return;
      }
    }
    if (part.startsWith("#") && sortedChannels.length > 0) {
      const chName = part.slice(1);
      const matchedChannel = sortedChannels.find((ch) => ch.name === chName);
      if (matchedChannel) {
        result.push(
          createElement(
            "span",
            {
              key: `${keyPrefix}-ch${i}`,
              className: `text-info rounded px-0.5 font-medium${onChannelClick ? " cursor-pointer hover:underline" : ""}`,
              ...(onChannelClick && {
                role: "button",
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onChannelClick(matchedChannel.id);
                },
              }),
            },
            part,
          ),
        );
        return;
      }
    }
    // Parse ::highlight:: in remaining text parts
    result.push(...parseHighlightSyntax(part, `${keyPrefix}-p${i}`));
  });
  return result;
}

/**
 * Parses `::highlight::` syntax into purple spans.
 * Passes through other text unchanged.
 */
function parseHighlightSyntax(
  text: string,
  keyPrefix: string,
): (string | React.ReactElement)[] {
  const regex = /::(.+?)::/g;
  const result: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let idx = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      // Parse URLs in text between highlights
      result.push(
        ...parseUrls(
          text.slice(lastIndex, match.index),
          `${keyPrefix}-t${idx}`,
        ),
      );
    }
    result.push(
      createElement(
        "span",
        {
          key: `${keyPrefix}-hl${idx}`,
          className: "text-primary font-medium",
        },
        match[1],
      ),
    );
    lastIndex = match.index + match[0].length;
    idx++;
  }

  if (lastIndex < text.length) {
    // Parse URLs in remaining text after last highlight
    result.push(...parseUrls(text.slice(lastIndex), `${keyPrefix}-t${idx}`));
  }
  return result.length > 0 ? result : parseUrls(text, keyPrefix);
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Detects URLs in text and wraps them in clickable <a> elements.
 * Matches protocol URLs (https://...) and bare domains (fever.energy, www.foo.com).
 * Same regex pattern as url-utils.ts bareDomainRegex.
 */
const URL_REGEX =
  /(?:https?:\/\/[^\s<>")\]]+|(?<![/@\w.-])(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?:\/[^\s<>")\]]*)?)/gi;

function parseUrls(
  text: string,
  keyPrefix: string,
): (string | React.ReactElement)[] {
  const result: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let idx = 0;
  let match: RegExpExecArray | null;
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    const url = match[0].replace(/[.,;:!?)]+$/, "");
    const strippedSuffix = match[0].slice(url.length);
    const fullUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    result.push(
      createElement(
        "a",
        {
          key: `${keyPrefix}-url${idx}`,
          href: fullUrl,
          className: "text-info hover:underline cursor-pointer",
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            openExternalUrl(fullUrl);
          },
        },
        url,
      ),
    );
    if (strippedSuffix) result.push(strippedSuffix);
    // Advance past the original match (not the trimmed url)
    lastIndex = match.index + match[0].length;
    idx++;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  return result.length > 0 ? result : [text];
}

interface MarkdownRendererProps {
  content: string;
  users: UserInfo[];
  onMentionClick?: (userId: string) => void;
  channels?: ChannelInfo[];
  onChannelClick?: (channelId: string) => void;
}

/**
 * Renders markdown content with mentions, channels, highlights, and links.
 * Uses react-markdown + remark-gfm + remark-breaks.
 */
export const MarkdownRenderer = memo(function MarkdownRenderer({
  content: rawContent,
  users,
  onMentionClick,
  channels,
  onChannelClick,
}: MarkdownRendererProps) {
  const content = useMemo(
    () => stripLegacyCardMarkers(rawContent),
    [rawContent],
  );
  const components = useMemo((): Components => {
    let textNodeCounter = 0;

    const processTextChildren = (children: ReactNode): ReactNode => {
      if (typeof children === "string") {
        const parsed = parseMentionsAndChannels(
          children,
          users,
          onMentionClick,
          channels,
          onChannelClick,
          `tn${textNodeCounter++}`,
        );
        return parsed.length === 1 && typeof parsed[0] === "string"
          ? parsed[0]
          : createElement("span", { key: `w${textNodeCounter}` }, ...parsed);
      }
      if (Array.isArray(children)) {
        return children.map((child, i) =>
          typeof child === "string"
            ? (() => {
                const parsed = parseMentionsAndChannels(
                  child,
                  users,
                  onMentionClick,
                  channels,
                  onChannelClick,
                  `tn${textNodeCounter++}`,
                );
                return parsed.length === 1 && typeof parsed[0] === "string"
                  ? parsed[0]
                  : createElement(
                      "span",
                      { key: `w${textNodeCounter}-${i}` },
                      ...parsed,
                    );
              })()
            : child,
        );
      }
      return children;
    };

    return {
      // Links: open externally, add protocol if missing
      a({ href, children }) {
        if (!href) return <span>{children}</span>;
        const fullHref = /^https?:\/\//i.test(href) ? href : `https://${href}`;
        return (
          <a
            href={fullHref}
            className="text-info hover:underline cursor-pointer"
            draggable
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openExternalUrl(fullHref);
            }}
            onDragStart={(e) => {
              e.dataTransfer.setData("text/uri-list", fullHref);
              e.dataTransfer.setData("text/plain", fullHref);
              e.dataTransfer.effectAllowed = "copy";
            }}
          >
            {children}
          </a>
        );
      },
      // react-markdown wraps fenced code blocks in `<pre><code>...`.
      // Our `code()` override below renders `<CodeBlock>` which itself
      // contains `<pre>`, so passing the outer `<pre>` through verbatim
      // would nest <pre><div><pre>...</pre></div></pre> — invalid HTML
      // and noisy CSS. Render the outer pre as a fragment so only
      // CodeBlock's pre survives.
      pre({ children }) {
        return <>{children}</>;
      },
      // Code: styled with mono font, NO mention parsing inside.
      // Block code → framed `CodeBlock` component (header bar with
      // language badge + copy + send-to-shell on hover, distinct bg
      // and border so the block reads as code at a glance — was a
      // bare `<code>` before, indistinguishable from monospace text).
      code({ children, className }) {
        const textContent =
          typeof children === "string"
            ? children
            : Array.isArray(children)
              ? children.map((c) => (typeof c === "string" ? c : "")).join("")
              : "";
        // Block-detection: react-markdown gives fenced blocks a
        // `language-XXX` class. Indented code (or unfenced ``` ``` `)
        // produces NO class, so also treat any multi-line `<code>` as
        // a block — otherwise such blocks would fall through to the
        // inline pill and lose all formatting.
        const langMatch = className?.match(/language-(\S+)/);
        const isBlock = !!langMatch || textContent.includes("\n");
        if (isBlock) {
          const language = langMatch?.[1] ?? null;
          return (
            <CodeBlock text={textContent} language={language}>
              {children}
            </CodeBlock>
          );
        }
        return (
          <code className="font-mono text-xs text-foreground bg-foreground/10 rounded px-1 py-0.5">
            {children}
          </code>
        );
      },
      // Text elements: parse mentions and highlights.
      //
      // Inline-formatting wrappers (`<strong>`, `<em>`, `<del>`,
      // `<blockquote>`) also pipe their text children through
      // `processTextChildren` so an `@mention` nested inside e.g.
      // `**...**` still gets highlighted and click-handled. Pre-fix the
      // composer used to emit `**@Name**` for any mention surrounded by
      // text (#1776), which surfaced as plain bold rendering with no
      // mention pill — those messages now render correctly without a
      // data backfill.
      p({ children }) {
        return <p>{processTextChildren(children)}</p>;
      },
      li({ children }) {
        return <li>{processTextChildren(children)}</li>;
      },
      strong({ children }) {
        return (
          <strong className="font-bold">{processTextChildren(children)}</strong>
        );
      },
      em({ children }) {
        // Giphy caption: render tiny and muted
        const text =
          typeof children === "string"
            ? children
            : Array.isArray(children)
              ? children.map((c) => (typeof c === "string" ? c : "")).join("")
              : "";
        if (text.startsWith("/giphy ")) {
          return (
            <em className="block text-[10px] text-muted-foreground/40 not-italic mt-0.5">
              {children}
            </em>
          );
        }
        return <em className="italic">{processTextChildren(children)}</em>;
      },
      img({ src, alt }) {
        // Pre-reserve height to prevent reflow when image loads.
        const reservedHeight = 200;
        return (
          <span
            className="inline-block max-w-full rounded-md overflow-hidden bg-secondary/30"
            style={{ maxHeight: 400, minHeight: reservedHeight }}
          >
            <img
              src={src}
              alt={alt || ""}
              className="max-w-full max-h-[400px] rounded-md object-contain"
              loading="lazy"
            />
          </span>
        );
      },
      del({ children }) {
        return <del>{processTextChildren(children)}</del>;
      },
      ul({ children }) {
        return (
          <ul
            style={{
              listStyleType: "disc",
              paddingLeft: "1.5em",
              margin: "0.25em 0",
            }}
          >
            {children}
          </ul>
        );
      },
      ol({ children }) {
        return (
          <ol
            style={{
              listStyleType: "decimal",
              paddingLeft: "1.5em",
              margin: "0.25em 0",
            }}
          >
            {children}
          </ol>
        );
      },
      blockquote({ children }) {
        return (
          <blockquote
            style={{
              borderLeft: "3px solid hsl(0 0% 16%)",
              paddingLeft: "0.75em",
              margin: "0.25em 0",
              color: "hsl(0 0% 63%)",
            }}
          >
            {processTextChildren(children)}
          </blockquote>
        );
      },
      // GFM tables — Tailwind preflight strips default table padding /
      // borders, so without these overrides remark-gfm produces a
      // collapsed-no-grid table where cells run together as plain text.
      table({ children }) {
        return (
          <table
            className="border-collapse my-1 text-[14px]"
            style={{ tableLayout: "auto" }}
          >
            {children}
          </table>
        );
      },
      thead({ children }) {
        return (
          <thead className="border-b border-foreground/20">{children}</thead>
        );
      },
      tbody({ children }) {
        return <tbody>{children}</tbody>;
      },
      tr({ children }) {
        return (
          <tr className="border-b border-foreground/10 last:border-0">
            {children}
          </tr>
        );
      },
      th({ children }) {
        return (
          <th className="font-semibold text-left px-2 py-1 align-top">
            {processTextChildren(children)}
          </th>
        );
      },
      td({ children }) {
        return (
          <td className="px-2 py-1 align-top">
            {processTextChildren(children)}
          </td>
        );
      },
    };
  }, [users, onMentionClick, channels, onChannelClick]);

  return (
    <div className="markdown-body w-full max-w-[34rem] text-[14px] font-normal text-muted-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});

/**
 * Strip legacy `<!--card:KIND-->{json}<!--/card-->` markers from
 * message content. Pre-Phase-2+3 producers wrapped card payloads in
 * these markers; the new path puts kind/payload in dedicated columns
 * and emits no markers. Existing rows in the DB still have them, and
 * the markdown library renders the comments as literal text rather
 * than HTML comments — so users see raw `<!--card:terminal-output-->{...}<!--/card-->`
 * in their scrollback. This strip is a backward-compat fix; new
 * messages don't go through it (no markers to find).
 *
 * Handles three shapes:
 *   1. Matched pair `<!--card:X-->...<!--/card-->` → strip the whole block.
 *   2. Orphan opening `<!--card:X-->` → strip the marker, keep payload.
 *   3. Orphan closing `<!--/card-->` → strip the marker.
 */
function stripLegacyCardMarkers(content: string): string {
  if (!content.includes("<!--card") && !content.includes("<!--/card")) {
    return content;
  }
  return content
    .replace(/<!--card:[^>]*?-->[\s\S]*?<!--\/card-->/g, "")
    .replace(/<!--card:[^>]*?-->/g, "")
    .replace(/<!--\/card-->/g, "")
    .trim();
}

/**
 * Render markdown content as React nodes.
 * Drop-in replacement for the old renderMentions() call.
 */
export function renderMarkdownContent(
  content: string,
  users: UserInfo[],
  onMentionClick?: (userId: string) => void,
  channels?: ChannelInfo[],
  onChannelClick?: (channelId: string) => void,
): ReactNode {
  return (
    <MarkdownRenderer
      content={content}
      users={users}
      onMentionClick={onMentionClick}
      channels={channels}
      onChannelClick={onChannelClick}
    />
  );
}
